import { useMutation } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Budget/redux/budget.selectors'
import {
  selectCreditCard,
  selectCreditCardPermissions,
} from 'src/app/BudgetVirtualCards/redux/budgetVirtualCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { connectedReplace } from 'src/navigation/Lnk'
import { accountVirtualCardsUrl } from 'src/navigation/urls'
import { reportDownloadsMutations } from 'src/queries/reportDownloads/reportDownloads.queries'
import { createSetIsDownloadSuccessPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

const BudgetVirtualCards: React.FC = () => {
  const translate = useTranslate()
  const creditCard = useSelector(selectCreditCard)
  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const creditCardPermissions = useSelector(selectCreditCardPermissions)

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const reportVirtualCardsDownloadMutation = useMutation(
    reportDownloadsMutations.virtualCards(),
  )
  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    reportVirtualCardsDownloadMutation.mutate(filters, {
      onSuccess: () => {
        dispatch(createSetIsDownloadSuccessPopupOpen(true))
      },
    })
  }

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard

  // redirect to account virtual cards if source
  useEffect(() => {
    if (creditCard?.isSource) {
      dispatch(connectedReplace(accountVirtualCardsUrl(creditCard.id)))
    }
  }, [creditCard])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('budgets virtual cards filter applied', {
        card_user: selectedFilters.card_user,
        card_type: selectedFilters.card_type,
        status: selectedFilters.status,
        search: selectedFilters.search,
        deactivating_in:
          selectedFilters.deactivating_in === undefined
            ? `anytime`
            : `${selectedFilters.deactivating_in}_days`,
      })
    }
  }, [selectedFilters])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          context={VirtualCardsListContext.Budget}
          creditCardId={creditCard?.id}
          onDownloadClick={
            canUpdateCreditCard
              ? dispatchPostReportVirtualCardsDownload
              : undefined
          }
          downloadPending={reportVirtualCardsDownloadMutation.isPending}
          defaultTitle={
            <ObjectBoxTitle
              title={translate('Virtual Cards Created')}
              static={{
                label: translate('Active'),
                count: activeVcnCount || 0,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive} // TODO: investigate this further
          totalCount={vcnCount || 0}
          readOnly={!canUpdateCreditCard}
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </div>
  )
}

export default BudgetVirtualCards
